import React, { useState, useEffect, useRef } from 'react';
import { FileText, Save, Eye, Edit3, ChevronRight, Upload, File, ExternalLink, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PAGES = [
  { id: 'faq', name: 'FAQ', icon: '❓', description: 'Frequently Asked Questions' },
  { id: 'terms', name: 'Terms of Service', icon: '📜', description: 'Terms and Conditions' },
  { id: 'privacy', name: 'Privacy Policy', icon: '🔒', description: 'Privacy and Data Policy' },
  { id: 'about', name: 'About Us', icon: '👥', description: 'About your company' },
];

const AdminContentManagement = () => {
  const [selectedPage, setSelectedPage] = useState(null);
  const [content, setContent] = useState({ title: '', content: '', pdf_url: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [preview, setPreview] = useState(false);
  const pdfInputRef = useRef(null);
  const { toast } = useToast();

  const loadPageContent = async (pageId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/content/${pageId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContent({
          title: data.title || '',
          content: data.content || '',
          pdf_url: data.pdf_url || ''
        });
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPage = (page) => {
    setSelectedPage(page);
    setPreview(false);
    loadPageContent(page.id);
  };

  const handleSave = async () => {
    if (!selectedPage) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/content/${selectedPage.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        toast({
          title: "Content Saved",
          description: `${selectedPage.name} has been updated successfully`,
        });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save content",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPage) return;

    if (file.type !== 'application/pdf') {
      toast({
        title: "Invalid File",
        description: "Only PDF files are allowed",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Maximum file size is 10MB",
        variant: "destructive"
      });
      return;
    }

    setUploadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload/content-pdf/${selectedPage.id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setContent(prev => ({ ...prev, pdf_url: data.url }));
        toast({
          title: "PDF Uploaded",
          description: `PDF has been uploaded for ${selectedPage.name}`,
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({
        title: "Upload Failed",
        description: "Could not upload PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const removePdf = () => {
    setContent(prev => ({ ...prev, pdf_url: '' }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-600">Edit your website pages (FAQ, Terms, Privacy, About)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Pages</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {PAGES.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => handleSelectPage(page)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      selectedPage?.id === page.id
                        ? 'bg-mint-100 text-mint-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-xl">{page.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{page.name}</p>
                      <p className="text-xs text-gray-500 truncate">{page.description}</p>
                    </div>
                    {selectedPage?.id === page.id ? (
                      <ChevronRight className="w-4 h-4" />
                    ) : null}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {selectedPage ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">{selectedPage.icon}</span>
                  {selectedPage.name}
                </CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreview(!preview)}
                  >
                    {preview ? (
                      <>
                        <Edit3 className="w-4 h-4 mr-1" />
                        Edit
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-mint-500 hover:bg-mint-600"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-500"></div>
                  </div>
                ) : preview ? (
                  <div className="prose max-w-none">
                    <h1>{content.title}</h1>
                    <div 
                      dangerouslySetInnerHTML={{ __html: content.content.replace(/\n/g, '<br/>') }}
                      className="whitespace-pre-wrap"
                    />
                    {content.pdf_url && (
                      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                        <a 
                          href={content.pdf_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-blue-600 hover:underline"
                        >
                          <File className="w-5 h-5" />
                          Download PDF Version
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Page Title</Label>
                      <Input
                        value={content.title}
                        onChange={(e) => setContent(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Page Title"
                      />
                    </div>

                    {/* PDF Upload Section */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <File className="w-4 h-4" />
                        PDF Document (Optional)
                      </Label>
                      <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                        {content.pdf_url ? (
                          <div className="flex items-center justify-between">
                            <a 
                              href={content.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:underline"
                            >
                              <File className="w-5 h-5" />
                              View uploaded PDF
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <div className="flex gap-2">
                              <input
                                ref={pdfInputRef}
                                type="file"
                                accept="application/pdf"
                                onChange={handlePdfUpload}
                                className="hidden"
                              />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => pdfInputRef.current?.click()}
                                disabled={uploadingPdf}
                              >
                                {uploadingPdf ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  'Replace'
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={removePdf}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <File className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                            <p className="text-sm text-gray-600 mb-2">
                              Upload a PDF version of this document
                            </p>
                            <input
                              ref={pdfInputRef}
                              type="file"
                              accept="application/pdf"
                              onChange={handlePdfUpload}
                              className="hidden"
                            />
                            <Button
                              variant="outline"
                              onClick={() => pdfInputRef.current?.click()}
                              disabled={uploadingPdf}
                            >
                              {uploadingPdf ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload PDF
                                </>
                              )}
                            </Button>
                            <p className="text-xs text-gray-500 mt-2">Max 10MB</p>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Users can download this PDF from the page. Great for detailed Terms of Service or Privacy Policy documents.
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Content (HTML supported)</Label>
                      <textarea
                        value={content.content}
                        onChange={(e) => setContent(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Enter your content here. You can use HTML tags for formatting."
                        className="w-full h-80 p-4 border rounded-lg font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-mint-500"
                      />
                      <p className="text-xs text-gray-500">
                        Tip: Use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;a href=""&gt; for formatting
                      </p>
                    </div>

                    {/* Quick formatting buttons */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <button
                        onClick={() => setContent(prev => ({ 
                          ...prev, 
                          content: prev.content + '\n<h2>Section Title</h2>\n' 
                        }))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                      >
                        + Heading
                      </button>
                      <button
                        onClick={() => setContent(prev => ({ 
                          ...prev, 
                          content: prev.content + '\n<p>Paragraph text here.</p>\n' 
                        }))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                      >
                        + Paragraph
                      </button>
                      <button
                        onClick={() => setContent(prev => ({ 
                          ...prev, 
                          content: prev.content + '\n<ul>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</ul>\n' 
                        }))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                      >
                        + List
                      </button>
                      <button
                        onClick={() => setContent(prev => ({ 
                          ...prev, 
                          content: prev.content + '\n<a href="https://">Link Text</a>\n' 
                        }))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                      >
                        + Link
                      </button>
                      <button
                        onClick={() => setContent(prev => ({ 
                          ...prev, 
                          content: prev.content + '\n<strong>Bold text</strong>\n' 
                        }))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded"
                      >
                        + Bold
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FileText className="w-12 h-12 mb-4 opacity-50" />
                <p>Select a page from the left to edit</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminContentManagement;
